import React, { useState, useEffect } from "react";
import DraggableBox from "./DraggableBox";
import { Input, Collapse } from "reactstrap";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import UnusedFields from "../../unusedfields/UnusedFields";
import {
  elementLists,
  layoutLists,
  moduleTypeOptions,
  customElementLists,
  customSectionOptions,
  accountSectionOptions,
  jobsSectionOptions,
  candidateSectionOptions,
  tosSectionOptions,
} from "./FormElementSidebarConstants";
import { categoryConstants } from "@workspace/template/src/components/TemplateBuilder/templateBuilderContants";
import {
  fetchJobForm,
  fetchJobFormReset,
} from "../../../../../../job/src/store/actions";

function FormElementSidebar({ setAllElements, unusedFields }) {
  const dispatch = useDispatch();

  const form = useSelector((state) => state.JobFormReducer.form);

  const [searchQuery, setSearchQuery] = useState("");
  const [isEIFOpen, setIsEIFOpen] = useState(true);
  const [isListOpen, setIsListOpen] = useState({
    unusedFields: false,
    elements: true,
    customElements: false,
    layout: true,
  });

  const customStyles = {
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
    control: (base, state) => ({
      ...base,
      borderColor: state.isFocused ? "#8AAED6" : "lightgray",
      "&:hover": {
        borderColor: state.isFocused ? "#0a56ae" : "lightgray",
      },
      backgroundColor: state.isDisabled ? "#EFF2F7" : "white",
    }),
    singleValue: (provided, state) => ({
      ...provided,
      color: state.isDisabled ? "black !important" : provided.color,
    }),
  };

  const [sections, setSections] = useState([]);
  const [selectedModuleType, setSelectedModuleType] = useState(null);
  const [selectedModuleSection, setSelectedModuleSection] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null);

  const unusedFieldsLength = (unusedFields) =>
    unusedFields.filter((field) => field.isUsed === false).length;

  useEffect(() => {
    dispatch(fetchJobFormReset());
  }, []);

  // Check section data
  useEffect(() => {
    if (selectedModuleType) {
      setSelectedModule(null);
      setAllElements(null);
      if (selectedModuleType?.label === categoryConstants.ACCOUNTS) {
        setSections(accountSectionOptions);
      } else if (selectedModuleType?.label === categoryConstants.JOBS) {
        setSections(jobsSectionOptions);
      } else if (selectedModuleType?.label === categoryConstants.CANDIDATES) {
        setSections(candidateSectionOptions);
      } else if (selectedModuleType?.label === categoryConstants.TOS) {
        setSections(tosSectionOptions);
      } else if (selectedModuleType?.label === "Custom") {
        setSections(customSectionOptions);
      }
    }
  }, [selectedModuleType]);

  useEffect(() => {
    if (selectedModuleSection) {
      if (selectedModuleType?.value === "Custom") {
        const elements = customElementLists?.sort((a, b) =>
          a?.draggableLabel?.localeCompare(b?.draggableLabel)
        );
        setSelectedModule(elements);
        setAllElements(elements);
      } else {
        dispatch(fetchJobForm(selectedModuleSection?.value));
      }
    }
  }, [selectedModuleSection]);

  useEffect(() => {
    if (form) {
      const elements = [];
      form?.formSchema
        ?.sort((a, b) => {
          if (a?.name && b?.name) {
            return a.name.localeCompare(b.name);
          }
          return 0;
        })
        ?.forEach((dt) => {
          if (dt?.name && dt?.name?.length > 0) {
            elements.push({
              draggableLabelIcon: "ri-file-copy-line",
              draggableLabel: convertToTitleCase(dt?.name),
              draggablePrefix: "element",
              draggableId: `${dt?.fieldId}-copied-${dt?.type}`,
              type: "field",
              data: dt,
            });
          }
        });
      setSelectedModule(elements);
      setAllElements(elements);
    } else {
      setSelectedModule(null);
      setAllElements(null);
    }
  }, [form]);

  const convertToTitleCase = (str) => {
    return str.replace(/([A-Z])/g, " $1").replace(/^./, function (s) {
      return s.toUpperCase();
    });
  };

  const handleModuleTypeChange = (selectedOption) => {
    setSelectedModuleType(selectedOption);
    setSelectedModuleSection(null);
  };

  return (
    <React.Fragment>
      <div className="d-flex flex-column justify-content-start gap-3 mt-2">
        <div className="d-flex justify-content-center align-items-baseline gap-2">
          <span
            className="bg-white d-flex align-items-center justify-content-center"
            style={{
              color: "#000",
              width: "25px",
              height: "25px",
              borderRadius: "100%",
            }}
          >
            {unusedFieldsLength(unusedFields)}
          </span>
          <span
            onClick={() => {
              setIsListOpen({
                ...isListOpen,
                unusedFields: !isListOpen.unusedFields,
              });
            }}
            className="cursor-pointer h6 fw-bold"
          >
            UNUSED FIELDS
          </span>
        </div>
        {isListOpen.unusedFields && unusedFields?.length > 0 && (
          <UnusedFields unusedFields={unusedFields} />
        )}
        {/* Search */}
        <div
          className="py-2"
          style={{
            backgroundColor: "#FCFCFD",
            position: "sticky",
            top: "0",
            zIndex: "1000",
          }}
        >
          <div className="search-box">
            <Input
              placeholder="Search"
              className="form-control box-shadow"
              style={{ borderColor: "#d3d3d3" }}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <span className="ri-search-line search-icon"></span>
          </div>
        </div>
        {/* Elements */}
        <div>
          <div
            className="d-flex flex-row justify-content-between align-items-center cursor-pointer"
            onClick={() => {
              setIsListOpen({
                ...isListOpen,
                elements: !isListOpen.elements,
              });
            }}
          >
            <span className="fw-semibold">Elements</span>
            <span
              className={`${
                isListOpen.elements ? "mdi mdi-menu-down" : "mdi mdi-menu-up"
              }`}
            ></span>
          </div>
          <Collapse isOpen={isListOpen.elements}>
            <div>
              {elementLists
                .filter((element) =>
                  element.draggableLabel
                    .toLowerCase()
                    .includes(searchQuery.toLowerCase())
                )
                .map((element) => (
                  <DraggableBox
                    key={element.draggableId}
                    draggableLabelIcon={element.draggableLabelIcon}
                    draggableLabel={element.draggableLabel}
                    draggablePrefix={element.draggablePrefix}
                    draggableId={element.draggableId}
                    type={element.type}
                  />
                ))}
              {elementLists.filter((element) =>
                element.draggableLabel
                  .toLowerCase()
                  .includes(searchQuery.toLowerCase())
              ).length === 0 && (
                <div className="text-start my-4">
                  <span>No elements found.</span>
                </div>
              )}
            </div>
          </Collapse>
        </div>
        <hr className="my-0" />
        {/* Existing Input Fields */}
        <div>
          <div
            className="d-flex flex-row justify-content-between align-items-center mb-2 cursor-pointer"
            onClick={() => setIsEIFOpen(!isEIFOpen)}
          >
            <span className="fw-semibold">Existing Input Fields</span>
            <span
              className={`${
                isEIFOpen ? "mdi mdi-menu-up" : "mdi mdi-menu-down"
              }`}
            ></span>
          </div>
          <Collapse isOpen={isEIFOpen}>
            <Select
              options={moduleTypeOptions}
              placeholder="Module Type"
              styles={customStyles}
              className="basic-multi-select mb-2"
              classNamePrefix={"select"}
              isClearable={true}
              onChange={handleModuleTypeChange}
            />

            <Select
              options={sections}
              placeholder="Module Section"
              styles={customStyles}
              className="basic-multi-select"
              classNamePrefix={"select"}
              isClearable={true}
              value={selectedModuleSection}
              onChange={(selectedOption) =>
                setSelectedModuleSection(selectedOption)
              }
            />

            {selectedModule?.length > 0 && (
              <div className="mt-3 ps-1">
                <div className="fw-light mb-2" style={{color: '#4E5761', fontSize: '12px'}}>Module Fields</div>
                {selectedModule.map((layout) => (
                  <DraggableBox
                    key={layout.draggableId}
                    draggableLabelIcon={layout.draggableLabelIcon}
                    draggableLabel={layout.draggableLabel}
                    draggablePrefix={layout.draggablePrefix}
                    draggableId={layout.draggableId}
                    type={layout.type}
                  />
                ))}
              </div>
            )}
          </Collapse>
        </div>
        <hr className="my-0" />
        {/* Layout */}
        <div>
          <div
            className="d-flex flex-row justify-content-between align-items-center mb-2 cursor-pointer"
            onClick={() => {
              setIsListOpen({
                ...isListOpen,
                layout: !isListOpen.layout,
              });
            }}
          >
            <span className="fw-semibold">Layout</span>
            <span
              className={`${
                isListOpen.layout ? "mdi mdi-menu-up" : "mdi mdi-menu-down"
              }`}
            ></span>
          </div>
          <Collapse isOpen={isListOpen.layout}>
            {layoutLists.map((layout) => (
              <DraggableBox
                draggableLabelIcon={layout.draggableLabelIcon}
                draggableLabel={layout.draggableLabel}
                draggablePrefix={layout.draggablePrefix}
                draggableId={layout.draggableId}
                type={layout.type}
              />
            ))}
          </Collapse>
        </div>
      </div>
    </React.Fragment>
  );
}

export default FormElementSidebar;
